import { getFilteredDrugsData } from '@common/TreatmentPlan/utils/filters';

import type { DrugData } from 'chaika-frontend-types/frontend-cms';
import { DrugStatus } from 'chaika-enums';

import { store } from '@store';
import { DateHelper } from '@utils/DateHelper';

export interface UseDrugData {
    currentEntryDrugs: DrugDataWithExtra[];
    futureEntryDrugs: DrugData[];
    activeEhrDrugs: DrugData[];
    otherDrugs: DrugData[];
    drugs: DrugData[];
    canceledOrExpiredDrugs: DrugData[];
}

export interface DrugDataWithExtra extends DrugData {
    isFuture?: boolean;
}

export function makeDrugsData(): UseDrugData {
    const { entry, ehr, drugStore } = store;
    const { isOnPrint, id: entryId, drugHistoryList, signedAt, creationTime } = entry;
    const filterParams = {
        isOnPrint,
        entryId,
        drugHistoryList,
        actualOn: signedAt ?? creationTime,
        fromSignedEntry: entry.isSigned,
    };

    const drugs = entry.drugList.values;
    const ehrDrugs = getFilteredDrugsData(
        {
            drugList: drugStore.drugList,
            drugs: ehr.medicalCases.drugsIds,
        },
        { ...filterParams, fromSignedEntry: false },
    );

    const currentEntryDrugs: DrugDataWithExtra[] = [];
    const futureEntryDrugs: DrugData[] = [];
    const otherDrugs: DrugData[] = [];
    const canceledOrExpiredDrugs: DrugData[] = [];

    const activeEhrDrugs: DrugData[] = [];

    drugs.forEach(it => {
        const isCancelledOrExpired =
            it.status === DrugStatus.Cancelled ||
            it.status === DrugStatus.CanceledByPatient ||
            it.status === DrugStatus.CancelledAppointment ||
            (!!it.end && DateHelper.isBefore(it.end, DateHelper.getStartOfDay(entry.visitTime)));

        if (isCancelledOrExpired) {
            return;
        }

        const isFuture = entry.visitTime
            ? !!it.start && DateHelper.isAfter(it.start, DateHelper.getEndOfDay(entry.visitTime))
            : !!it.start && DateHelper.isAfter(it.start, DateHelper.getEndOfDay(DateHelper.now()));

        if (it.parentEntryId === entry.id) {
            currentEntryDrugs.push({ ...it, isFuture });
        } else if (isFuture) {
            futureEntryDrugs.push(it);
        } else {
            otherDrugs.push(it);
        }
    });

    ehrDrugs.forEach(ehrDrug => {
        const isActive =
            ehrDrug.status !== DrugStatus.Cancelled &&
            ehrDrug.status !== DrugStatus.CanceledByPatient &&
            ehrDrug.status !== DrugStatus.CancelledAppointment &&
            (!!ehrDrug?.end
                ? DateHelper.isSameDayOrAfter(ehrDrug.end, DateHelper.getStartOfDay(entry.visitTime))
                : true);

        if (
            isActive &&
            !currentEntryDrugs.some(current => current.id === ehrDrug.id) &&
            !otherDrugs.some(current => current.id === ehrDrug.id) &&
            !futureEntryDrugs.some(current => current.id === ehrDrug.id) &&
            DateHelper.isAfter(entry.creationTime, ehrDrug.createdAt)
        ) {
            activeEhrDrugs.push(ehrDrug);
        }
    });

    return {
        futureEntryDrugs,
        currentEntryDrugs,
        activeEhrDrugs,
        otherDrugs,
        drugs,
        canceledOrExpiredDrugs,
    };
}
